import React, { useEffect, useState, useMemo } from 'react';

const NPCDetailedListingsPage = () => {
  const [properties, setProperties] = useState([]);
  const [sortField, setSortField] = useState('sharedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [rowIndexMap, setRowIndexMap] = useState({});

  const isAdmin = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
      return user?.role === 'admin';
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/public/property/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        postedWithinDays: 7,
        sort: { createdAt: -1 },
        limit: 1000
      })
    })
      .then(res => res.json())
      .then(data => {
        setProperties(data.results || []);
        const newMap = {};
        (data.results || []).forEach((prop, idx) => {
          newMap[prop._id] = idx + 1;
        });
        setRowIndexMap(newMap);
      })
      .catch(err => console.error('Failed to fetch listings', err));
  }, []);

  const sortedProperties = [...properties].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];

    if (aValue === undefined || bValue === undefined) return 0;
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    return 0;
  });

  const renderSortableHeader = (label, field) => {
    const isActive = sortField === field;
    const directionSymbol = isActive ? (sortDirection === 'asc' ? '▲' : '▼') : '';

    return (
      <th
        className={`border px-2 py-1 cursor-pointer select-none whitespace-nowrap ${isActive ? 'bg-blue-100 font-semibold' : ''}`}
        onClick={() => {
          if (isActive) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
          } else {
            setSortField(field);
            setSortDirection('asc');
          }
        }}
      >
        {label} {directionSymbol}
      </th>
    );
  };

  return (
    <div className="w-full px-4 py-6">
      <div className="bg-white p-4 border rounded shadow overflow-x-auto">
        <h2 className="text-md font-semibold mb-2">📊 Detailed Listings (Last 7 Days)</h2>
        <table className="w-full text-xs table-auto font-mono border-collapse border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-2 py-1">#</th>
              <th className="border px-2 py-1">Address</th>
              {renderSortableHeader('Min Price', 'askingPriceMin')}
              {renderSortableHeader('Max Rent', 'rentPerWeekMax')}
              {renderSortableHeader('Beds', 'bedrooms')}
              <th className="border px-2 py-1">Baths</th>
              <th className="border px-2 py-1">Land</th>
              <th className="border px-2 py-1">Built</th>
              {renderSortableHeader('Gross Yield', 'rentalYieldPercent')}
              <th className="border px-2 py-1">Tags</th>
              {renderSortableHeader('Shared At', 'sharedAt')}
              {isAdmin && <th className="border px-2 py-1">Shared By</th>}
            </tr>
          </thead>
          <tbody>
            {sortedProperties.map((p) => (
              <tr key={p._id}>
                <td className="border px-2 py-1 text-center">{rowIndexMap[p._id] || '—'}</td>
                <td className="border px-2 py-1">
                  <a
                    href="/brief"
                    className="text-blue-600 hover:underline"
                    title="Open client brief form"
                  >
                    {p.address || p.addressPreview || '—'}
                  </a>
                </td>
                <td className="border px-2 py-1">{p.askingPriceMin || p.askingPrice || '—'}</td>
                <td className="border px-2 py-1">{p.rentPerWeek || p.rental || '—'}</td>
                <td className="border px-2 py-1">{p.bedrooms ?? '—'}</td>
                <td className="border px-2 py-1">{p.bathrooms ?? '—'}</td>
                <td className="border px-2 py-1">
                  {p.landSizeNumeric ? `${p.landSizeNumeric} m²` : (p.landSize || '—')}
                </td>
                <td className="border px-2 py-1">{p.yearBuiltNumeric || p.yearBuilt || '—'}</td>
                <td className="border px-2 py-1">
                  {p.rentalYieldPercent ? `${p.rentalYieldPercent}%` : (p.rentalYield || '—')}
                </td>
                <td className="border px-2 py-1">
                  {(p.tags || []).map(t => t.name).join(', ') || '—'}
                </td>
                <td className="border px-2 py-1">
                  {p.sharedAt ? new Date(p.sharedAt).toLocaleString() : '—'}
                </td>
                {isAdmin && <td className="border px-2 py-1">{p.sharedBy || '—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NPCDetailedListingsPage;
